import { bindDialog, BindDialogReturn, DialogProps, DialogResult, DialogState, useDialogState } from "../../hooks/use-dialog-state/UseDialogState";
import { AddAPhoto } from "@mui/icons-material";
import { IconButton, MenuItem, Stack, Typography } from "@mui/material";
import { default as React, useEffect, useRef, useState, type JSX } from "react";
import { useFieldArray, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ApplicationMimeTypesEnum } from "../../common/enums/ApplicationMimeTypesEnum";
import { FilestoreTypeEnum } from "../../common/enums/FilestoreTypeEnum";
import { ResourceTypeEnum } from "../../common/enums/ResourceTypeEnum";
import { IeLynxObject } from "../../common/models/eLynxObject";
import { IAttachment } from "../../common/models/IAttachment";
import { getAttachmentUrl } from "../../common/utilities/AttachmentHelpers";
import { valueOrDefault } from "../../common/utilities/ValueOrDefault";
import { useConfirmation } from "../../contexts/confirmation-context/ConfirmationContext";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useRouteContext } from "../../hooks/use-route-context/UseRouteContext";
import { useRunTicketTanks } from "../../queries/run-tickets/UseRunTicketTanks";
import { useSaveRunTicket } from "../../queries/run-tickets/UseSaveRunTicket";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { IRunTicketModel } from "../../services/run-tickets-data-service/Models";
import { bindCameraDialog, CameraDialog, useCameraDialogState } from "../camera-dialog/CameraDialog";
import FileDownloader, { FileDownloaderType } from "../file-downloader/FileDownloader";
// import FileSelectDialog, { FileSelectDialogInitValues, FileSelectDialogResultValues } from "../file-select-dialog/FileSelectDialog";
import FileViewerDialog, { bindFileViewerDialog, useFileViewerDialogState } from "../file-viewer-dialog/FileViewerDialog";
import FormAttachmentCard, { FormAttachmentActionCardEnum } from "../forms/form-attachment-card/FormAttachmentCard";
import FormDateTimePicker from "../forms/form-date-time-picker/FormDateTimePicker";
import { FormFeetAndInchesPicker } from "../forms/form-feet-and-inches-picker/FormFeetAndInchesPicker";
import FormInput from "../forms/form-input/FormInput";
import FormPropertySection from "../forms/form-property-section/FormPropertySection";
import FormProperty from "../forms/form-property/FormProperty";
import FormSelect from "../forms/form-select/FormSelect";
import Form from "../forms/form/Form";
import ResponsiveDialog from "../responsive-dialog/ResponsiveDialog";
import UnsavedChangesPrompt from "../unsaved-changes-prompt/UnsavedChangesPrompt";
import { differenceInHours } from "date-fns";

export type RunTicketEditDialogInitValues = {
    runTicket: IRunTicketModel;
    timestamp: Date;

    forTank?: boolean;
    minDate?: Date;
    maxDate?: Date;
};

export type RunTicketEditDialogResult = DialogResult<IRunTicketModel>;
export type RunTicketEditDialogProps = DialogProps<RunTicketEditDialogInitValues, RunTicketEditDialogResult>;

type RunTicketEditDialogFormValuesType = {
    tankID: number;
    ticketNumber: string | null;
    purchaserName: string | null;
    comments: string | null;
    startDateTime: Date;
    tankLevelOpen: number | null;
    interfaceLevelOpen: number | null;
    temperatureTop?: number | null;
    sealOff?: string | null;
    completeDateTime: Date;
    tankLevelClose: number | null;
    interfaceLevelClose?: number | null;
    temperatureBottom?: number | null;
    sealOn?: string | null;
    specificGravity?: number | null;
    basicSedimentsAndWater?: number | null;
    tankLevelEstimatedHaul?: number | null;
    interfaceLevelEstimatedHaul?: number | null;
    attachments: Array<IAttachment>;
};

const defaultTimestamp = new Date();

const defaultFormValues: RunTicketEditDialogFormValuesType = {
    tankID: -1,
    ticketNumber: "",
    purchaserName: "",
    comments: "",
    startDateTime: defaultTimestamp,
    tankLevelOpen: null,
    interfaceLevelOpen: null,
    temperatureTop: null,
    sealOff: "",
    completeDateTime: defaultTimestamp,
    tankLevelClose: null,
    interfaceLevelClose: null,
    temperatureBottom: null,
    sealOn: "",
    specificGravity: null,
    basicSedimentsAndWater: null,
    tankLevelEstimatedHaul: null,
    interfaceLevelEstimatedHaul: null,
    attachments: new Array<IAttachment>()
};

enum InputModeEnum {
    Either = 0,
    Level = 1,
    Haul = 2
}

export default function RunTicketEditDialog({
    open,
    onClose,
    values,
}: RunTicketEditDialogProps): JSX.Element {

    const routeContext = useRouteContext();
    const [userState] = useUserContext();
    const [offlineState] = useOfflineDetectContext();
    const { t } = useTranslation();

    const runTicket = values?.runTicket;
    const isAddMode = (runTicket?.id ?? -1) <= -1;
    const forTank = values?.forTank;
    const minDate = values?.minDate;
    const maxDate = values?.maxDate;

    const cameraDialog = useCameraDialogState();
    const fileViewerDialog = useFileViewerDialogState();
    // const fileSelectDialog = useFileSelectDialogState;
    const fileDownloaderRef = useRef<FileDownloaderType>(null);
    const [inputMode, setInputMode] = useState<InputModeEnum>(InputModeEnum.Either);
    const { confirm, acknowledge } = useConfirmation();

    const { data: tanks/*, status*/ } = useQueryNotifications(useRunTicketTanks)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject as IeLynxObject,
        offline: offlineState.offline
    });

    const { mutate: saveRunTicket/*, status*/ } = useQueryNotifications(useSaveRunTicket)({
        customerID: userState.customer?.id as number,
        groupID: routeContext.groupID as number,
        contextObject: routeContext.contextObject as IeLynxObject,
        offline: offlineState.offline,
        timestamp: values?.timestamp as Date,
        onSuccess: () => {
            acknowledge(t("saveSuccessMessage", { itemName: t("dataTypeNames.runTicket") }));
        }
    });

    const form = useForm<RunTicketEditDialogFormValuesType>({
        mode: "onChange",
        defaultValues: defaultFormValues
    });

    const {
        control,
        setValue,
        getValues,
        handleSubmit,
        formState: {
            isDirty,
            isValid,
            errors,
            dirtyFields
        },
        reset,
        watch,
        clearErrors,
        trigger
    } = form;

    const {
        fields: attachments,
        append: appendAttachments,
        remove: removeAttachment
    } = useFieldArray({
        control: control,
        name: "attachments",
        keyName: "fieldKey"
    });

    useEffect(() => {
        if (open) {

            if (runTicket) {
                reset(toFormValuesType(runTicket));

                if (isAddMode) {
                    setInputMode(InputModeEnum.Either);

                    // if the device or tank battery has only has one associated tank, set the tankID to that tank
                    if ((tanks?.length ?? 0) === 1) {
                        const tankID = tanks?.[0].id ?? -1;
                        if (tankID > 0) {
                            setValue("tankID", tankID);
                        }
                    }

                } else {
                    setInputMode(InputModeEnum.Level);
                }
            }
        } else {

            reset(defaultFormValues);
        }
    }, [
        reset,
        runTicket,
        open,
        isAddMode,
        tanks,
        setValue
    ]);

    async function onOkay(data: RunTicketEditDialogFormValuesType) {

        if (dirtyFields.completeDateTime || dirtyFields.startDateTime) {

            const hoursToComplete = differenceInHours(data.completeDateTime, data.startDateTime);
            if (hoursToComplete > 1) {

                const result = await confirm({
                        dialogTitle: '',
                        yesButtonText: t("buttons.yesContinue"),
                        noButtonText: t("buttons.noCancel"),
                        confirmationText:  t('runTicketDialog.differenceInHoursWarning', { value: hoursToComplete }),
                        showConfirmationText: true,
                        showYesButton: true,
                        showNoButton: true
                    });
        
                if (result !== "yes") {
                    return;
                }
            }
        }
        
        const updatedRunTicket = toIRunticket(runTicket, tanks ?? [], data);

        saveRunTicket(updatedRunTicket);

        // reset(defaultFormValues);

        onClose && onClose({
            reason: "ok",
            values: updatedRunTicket
        });
    }

    async function onCancel() {

        const canCancel = isDirty
            ? await getUserConfirmation()
            : true;

        if (canCancel) {
            // reset(defaultFormValues);
            onClose && onClose({ reason: "cancel" });
        }
    }

    async function getUserConfirmation() {

        return (await confirm({
            dialogTitle: t("cancelConfirmation.title"),

            confirmationText: isAddMode
                ? t("runTicketDialog.cancelAddText")
                : t("runTicketDialog.cancelEditText"),

            yesButtonText: t("cancelConfirmation.yesButtonText"),
            noButtonText: t("cancelConfirmation.noButtonText"),
            showConfirmationText: true,
            showYesButton: true,
            showNoButton: true
        })) === "yes";
    }

    async function onOpenCamera() {
        const result = await cameraDialog.open();

        if (result.reason === "ok") {
            const regex = /-([0-9]*?)\./;
            const maxNumber = attachments
                .filter(i => i.fileName?.startsWith("Image"))
                .reduce<number>((prev, cur) => {

                    const fileName = cur.fileName ?? "";
                    const matched = regex.exec(fileName);

                    if (matched && matched.length > 0) {
                        const num: number = parseInt(matched[1], 10);
                        return num > prev ? num : prev;
                    }

                    return prev;
                }, 0);

            const fileName = `Image-${maxNumber < 9 ? 0 : ""}${maxNumber + 1}`;

            const attachment: IAttachment = {
                id: -1,
                fileName: `${fileName}.jpg`,
                description: fileName,
                deleted: false,
                filestoreType: FilestoreTypeEnum.Images,
                lastModified: undefined,
                mimeType: ApplicationMimeTypesEnum.JPEG,
                base64Data: result.values?.base64imageData,
                url: undefined,
                thumbnailUrl: undefined
            } as IAttachment;

            appendAttachments(attachment);
        }
    }

    // async function onOpenFiles() {
    //     const results = await fileSelectDialog.open();

    //     const attachments = results.values?.files.map(file => {
    //         const url = URL.createObjectURL(file);
    //         const attachment: IAttachment = {
    //             id: -1,
    //             fileName: file.name,
    //             description: file.name,
    //             deleted: false,
    //             //filestoreType: FilestoreTypeEnum.Images,
    //             //lastModified?: string;
    //             mimeType: ApplicationMimeTypesEnum.JPEG,
    //             //base64Data: result.values?.imageUrl,
    //             url: url,
    //             thumbnailUrl: url
    //         } as IAttachment;
    //         return attachment;
    //     }) ?? new Array<IAttachment>();

    //     appendAttachments(attachments ?? new Array<IAttachment>());
    // }

    async function onAttachmentAction(action: FormAttachmentActionCardEnum, index: number, attachment: IAttachment) {
        if (action === FormAttachmentActionCardEnum.View) {

            await fileViewerDialog.open({
                title: attachment.description,
                url: getUrl(attachment.base64Data, attachment.url),
                mimeType: attachment.mimeType
            });
        } else if (action === FormAttachmentActionCardEnum.Delete) {

            removeAttachment(index);
        } else if (action === FormAttachmentActionCardEnum.Download) {

            fileDownloaderRef.current?.download(getAttachmentUrl(attachment.base64Data, attachment.url), attachment.fileName);
        }
    }

    function getUrl(base64Data: string | undefined, url: string | undefined): string | undefined {
        return base64Data
            ? `data:image/jpeg;base64,${base64Data}`
            : url;
    }

    function validateTankLevelOpen(tankLevelOpen: number, heightInInches?: number): boolean | string {
        if (inputMode === InputModeEnum.Level || userState.userSettings?.tankLevelInches) {
            const [interfaceLevelOpen, tankLevelClose] = getValues(["interfaceLevelOpen", "tankLevelClose"]);

            if ((Number.isFinite(tankLevelClose) && tankLevelOpen < (tankLevelClose as number)) ||
                (Number.isFinite(interfaceLevelOpen) && tankLevelOpen < (interfaceLevelOpen as number))) {
                return `The starting top level must be greater than the ending top level and greater than the starting interface level`;
            }

            if (userState.userSettings?.tankLevelInches && (interfaceLevelOpen ?? 0) + tankLevelOpen > (heightInInches ?? 0)) {
                return `The starting top level plus the starting interface level cannot be greater than the tank height ${heightInInches} inches.`;
            }

            if (errors.tankLevelOpen) {
                clearErrors("tankLevelOpen");
            }
        }

        return true;
    }

    function validateInterfaceLevelOpen(interfaceLevelOpen: number, heightInInches?: number): boolean | string {
        if (inputMode === InputModeEnum.Level || userState.userSettings?.tankLevelInches) {
            const [tankLevelOpen] = getValues(["tankLevelOpen"]);

            if (Number.isFinite(tankLevelOpen) && interfaceLevelOpen > (tankLevelOpen as number)) {
                return `The starting interface level must be less than the starting top level`;
            }


            if (userState.userSettings?.tankLevelInches && (tankLevelOpen ?? 0) + interfaceLevelOpen > (heightInInches ?? 0)) {
                return `The starting top level plus the starting interface level cannot be greater than the tank height ${heightInInches} inches.`;
            }



            if (errors.interfaceLevelOpen) {
                clearErrors("interfaceLevelOpen");
            }
        }

        return true;
    }

    function validateTankLevelClose(tankLevelClose: number, heightInInches?: number): boolean | string {
        if (inputMode === InputModeEnum.Level || userState.userSettings?.tankLevelInches) {
            const [interfaceLevelClose, tankLevelOpen] = getValues(["interfaceLevelClose", "tankLevelOpen"]);

            if ((Number.isFinite(tankLevelOpen) && tankLevelClose > (tankLevelOpen as number)) ||
                (Number.isFinite(interfaceLevelClose) && tankLevelClose < (interfaceLevelClose as number))) {
                return `The ending top level must be less than the starting top level and greater than the ending interface level`;
            }

            if (userState.userSettings?.tankLevelInches && tankLevelClose + (interfaceLevelClose ?? 0) > (heightInInches ?? 0)) {
                return `The closing top level plus the closing interface level cannot be greater than the tank height ${heightInInches} inches.`;
            }


            if (errors.tankLevelClose) {
                clearErrors("tankLevelClose");
            }
        }

        return true;
    }

    function validateInterfaceLevelClose(interfaceLevelClose: number, heightInInches?: number): boolean | string {
        if (inputMode === InputModeEnum.Level || userState.userSettings?.tankLevelInches) {
            const [tankLevelClose] = getValues(["tankLevelClose"]);

            if (Number.isFinite(tankLevelClose) && interfaceLevelClose > (tankLevelClose as number)) {
                return `The ending interface level must be less than the ending top level`;
            }

            if (userState.userSettings?.tankLevelInches && (tankLevelClose ?? 0) + interfaceLevelClose > (heightInInches ?? 0)) {
                return `The closing top level plus the closing interface level cannot be greater than the tank height ${heightInInches} inches.`;
            }


            if (errors.interfaceLevelClose) {
                clearErrors("interfaceLevelClose");
            }

        }

        return true;
    }

    function onTankChanged() {
        if (inputMode === InputModeEnum.Either || inputMode === InputModeEnum.Level) {
            calculateHauls();
        } else if (inputMode === InputModeEnum.Haul) {
            calculateLevels();
        }
    }

    function onLevelChanged() {
        if (inputMode === InputModeEnum.Either || inputMode === InputModeEnum.Level) {
            calculateHauls();
        }
    }

    function onHaulChanged() {
        if ((inputMode === InputModeEnum.Either || inputMode === InputModeEnum.Haul)) {
            calculateLevels();
        }
    }

    function calculateHauls() {
        let tankLevelEstimatedHaul: number | null = null;
        let interfaceLevelEstimatedHaul: number | null = null;

        const [
            tankID, tankLevelOpen, interfaceLevelOpen, tankLevelClose, interfaceLevelClose
        ] = getValues([
            "tankID",
            "tankLevelOpen",
            "interfaceLevelOpen",
            "tankLevelClose",
            "interfaceLevelClose"
        ]);

        if (Number.isFinite(tankLevelOpen) ||
            Number.isFinite(tankLevelClose) ||
            Number.isFinite(interfaceLevelOpen) ||
            Number.isFinite(interfaceLevelClose)) {

            const tank = tanks?.find(tank => tank.id === tankID);

            let interfaceDelta: number | null = 0.0;

            if (Number.isFinite(interfaceLevelOpen) && Number.isFinite(interfaceLevelClose) && tank?.isDualPhase) {

                interfaceDelta = (interfaceLevelOpen as number) - (interfaceLevelClose as number);
                interfaceLevelEstimatedHaul = formatValue(interfaceDelta * (tank?.tankStyle?.inchesMultiplier ?? 0), 0);
            }

            if (Number.isFinite(tankLevelOpen) && Number.isFinite(tankLevelClose)) {

                const delta = (tankLevelOpen as number) - (tankLevelClose as number);
                tankLevelEstimatedHaul = formatValue((delta - interfaceDelta) * (tank?.tankStyle?.inchesMultiplier ?? 0), 0);
            }

            if (inputMode === InputModeEnum.Either) {
                setInputMode(InputModeEnum.Level);
            }

            function formatValue(value: number, decimals: number): number {
                return Math.round(value * Math.pow(10, decimals)) / Math.pow(10, decimals);
            }

        } else {

            if (inputMode === InputModeEnum.Level) {
                setInputMode(InputModeEnum.Either);
            }
        }

        setValue("tankLevelEstimatedHaul", tankLevelEstimatedHaul);
        setValue("interfaceLevelEstimatedHaul", interfaceLevelEstimatedHaul);
        trigger([
            "tankLevelEstimatedHaul",
            "interfaceLevelEstimatedHaul"
        ]);
    }

    function calculateLevels() {
        let tankLevelOpen: number | null = null;
        let tankLevelClose: number | null = null;
        let interfaceLevelOpen: number | null = null;
        let interfaceLevelClose: number | null = null;

        const [
            tankID, tankLevelEstimatedHaul, interfaceLevelEstimatedHaul
        ] = getValues([
            "tankID",
            "tankLevelEstimatedHaul",
            "interfaceLevelEstimatedHaul"
        ]);

        if (Number.isFinite(tankLevelEstimatedHaul) ||
            Number.isFinite(interfaceLevelEstimatedHaul)) {

            const tank = tanks?.find(tank => tank.id === tankID);

            if (Number.isFinite(interfaceLevelEstimatedHaul && tank?.tankStyle?.inchesMultiplier)) {
                interfaceLevelOpen = getInchesFromBarrels(interfaceLevelEstimatedHaul as number, tank?.tankStyle?.inchesMultiplier as number);
                interfaceLevelClose = 0;
            }

            if (Number.isFinite(tankLevelEstimatedHaul && tank?.tankStyle?.inchesMultiplier)) {
                tankLevelOpen = getInchesFromBarrels(tankLevelEstimatedHaul as number, tank?.tankStyle?.inchesMultiplier as number) + (interfaceLevelOpen ?? 0);
                tankLevelClose = 0;
            }

            if (inputMode === InputModeEnum.Either) {
                setInputMode(InputModeEnum.Haul);
            }
        }
        else {
            if (inputMode === InputModeEnum.Haul) {
                setInputMode(InputModeEnum.Either);
            }
        }

        setValue("tankLevelOpen", tankLevelOpen);
        setValue("tankLevelClose", tankLevelClose);
        setValue("interfaceLevelOpen", interfaceLevelOpen);
        setValue("interfaceLevelClose", interfaceLevelClose);
        trigger([
            "tankLevelOpen",
            "tankLevelClose",
            "interfaceLevelOpen",
            "interfaceLevelClose"
        ]);

        function getInchesFromBarrels(value: number, inchesMultiplier: number) {
            var level = value / inchesMultiplier;
            return Math.round(level * Math.pow(10, 2)) / Math.pow(10, 2);
        };
    }

    function _validateDates(start: Date, end: Date) : boolean | string {

        if (end.valueOf() < start.valueOf()) {
            return "The 2nd measurement timestamp cannot be before the 1st measurement timestamp.";
        }

        clearErrors(['startDateTime', 'completeDateTime']);

        return true;
    }

    function validateStartDate(start: Date): boolean | string {

        const end = getValues('completeDateTime');
        return _validateDates(start, end);
    }


    function validateEndDate(end: Date): boolean | string {

        const start = getValues('startDateTime');
        return _validateDates(start, end);
    }

    // console.log(`========== Rendering RunTicketDialog ========== isDirty=${isDirty} isValid=${isValid}`);
    const tankID = watch("tankID");
    const tank = tanks?.find(tank => tank.id === tankID);

    return (
        <>
            <ResponsiveDialog
                title={isAddMode ? t("runTicketDialog.addRunTicket") : t("runTicketDialog.editRunTicket")}
                enableOkay={isDirty && isValid}
                okayLabel={t("buttons.save")}
                open={open}
                onClose={
                    async (reason: string) => {
                        reason === "ok"
                            ? handleSubmit(async (data) => onOkay(data))()
                            : onCancel();
                    }
                }
            >
                {/* <LoadingZone isLoading={status==="loading"}> */}
                <Form form={form}>
                    <FormPropertySection label="">
                        <FormProperty>
                            <FormSelect
                                name="tankID"
                                label={t("runTicket.tank")}
                                sx={{
                                    width: 1
                                }}
                                // rules={{
                                //     required: true,
                                //     min: 0
                                // }}
                                disabled={(tanks?.length ?? 0) === 0 }
                                readOnly={!isAddMode || tanks?.length === 1 || forTank}
                                onChange={onTankChanged}
                            >
                                <MenuItem key={-1} value={-1} disabled={true}>(Select Tank)</MenuItem>
                                {tanks?.map(o =>
                                    <MenuItem key={o.id} value={o.id}>{o.displayText}</MenuItem>
                                )}
                            </FormSelect>
                        </FormProperty>
                        <FormProperty>
                            <FormInput
                                name="ticketNumber"
                                label={t("runTicket.ticketNumber")}
                                sx={{
                                    width: 1
                                }}
                                rules={{
                                    required: {
                                        value: true,
                                        message: "A Ticket Number is required"
                                    }
                                }}
                                disabled={tankID === -1}
                            />
                        </FormProperty>
                    </FormPropertySection>

                    <FormPropertySection label={t("runTicket.firstMeasurement")}>
                        <FormProperty>
                            {!userState.userSettings?.tankLevelInches ?
                                <FormFeetAndInchesPicker
                                    name="tankLevelOpen"
                                    label={t("runTicket.topLevel")}
                                    maxFeet={tank ? (tank.tankStyle.heightInInches / 12) : 12}
                                    sx={{
                                        width: 1
                                    }}
                                    disabled={tankID === -1}
                                    rules={{
                                        required: true,
                                        validate: validateTankLevelOpen
                                    }}
                                    readOnly={inputMode === InputModeEnum.Haul}
                                    onChange={onLevelChanged}
                                />

                                :

                                <FormInput
                                    name="tankLevelOpen"
                                    label={t("runTicket.topLevelInches")}
                                    numeric={true}
                                    sx={{
                                        width: 1
                                    }}
                                    disabled={tankID === -1}
                                    rules={{
                                        required: true,
                                        validate: (val: number) => {return validateTankLevelOpen(val, tank!.tankStyle.heightInInches)} 
                                    }}
                                    readOnly={inputMode === InputModeEnum.Haul}
                                    onChange={onLevelChanged}
                                />
                            }
                        </FormProperty>
                        {tank?.isDualPhase &&
                            <FormProperty>
                                {!userState.userSettings?.tankLevelInches ?
                                <FormFeetAndInchesPicker
                                    name="interfaceLevelOpen"
                                    label={t("runTicket.interfaceLevel")}
                                    maxFeet={tank ? (tank.tankStyle.heightInInches / 12) : 12}
                                    sx={{
                                        width: 1
                                    }}
                                    disabled={tankID === -1}
                                    rules={{
                                        validate: validateInterfaceLevelOpen
                                    }}
                                    readOnly={inputMode === InputModeEnum.Haul}
                                    onChange={onLevelChanged}
                                />
                                :

                                <FormInput
                                    name="interfaceLevelOpen"
                                    label={t("runTicket.interfaceLevelInches")}
                                    numeric={true}
                                    sx={{
                                        width: 1
                                    }}
                                    disabled={tankID === -1}
                                    rules={{
                                        required: true,
                                        validate: (val: number) => { return validateInterfaceLevelOpen(val, tank!.tankStyle.heightInInches)} 
                                    }}
                                    readOnly={inputMode === InputModeEnum.Haul}
                                    onChange={onLevelChanged}
                                />
                                }

                            </FormProperty>
                        }
                        <FormProperty>
                            <FormDateTimePicker
                                name="startDateTime"
                                label={t("runTicket.timestamp")}
                                sx={{
                                    width: 1
                                }}
                                rules={{
                                    required: true,
                                    validate: validateStartDate

                                }}
                                disabled={tankID === -1}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        </FormProperty>
                        <FormProperty>
                            <FormInput
                                name="temperatureTop"
                                label={t("runTicket.temperature")}
                                numeric={true}
                                sx={{
                                    width: 1
                                }}
                                disabled={tankID === -1}
                            />
                        </FormProperty>
                        <FormProperty>
                            <FormInput
                                name="sealOff"
                                label={t("runTicket.sealOff")}
                                sx={{
                                    width: 1
                                }}
                                disabled={tankID === -1}
                            />
                        </FormProperty>
                    </FormPropertySection>

                    <FormPropertySection label={t("runTicket.secondMeasurement")}>
                        <FormProperty>
                            {!userState.userSettings?.tankLevelInches ?
                            <FormFeetAndInchesPicker
                                name="tankLevelClose"
                                label={t("runTicket.topLevel")}
                                maxFeet={tank ? (tank.tankStyle.heightInInches / 12) : 12}
                                sx={{
                                    width: 1
                                }}
                                disabled={tankID === -1}
                                rules={{
                                    required: true,
                                    validate: validateTankLevelClose
                                }}
                                readOnly={inputMode === InputModeEnum.Haul}
                                onChange={onLevelChanged}
                            />
                            :
                                <FormInput
                                    name="tankLevelClose"
                                    label={t("runTicket.topLevelInches")}
                                    numeric={true}
                                    sx={{
                                        width: 1
                                    }}
                                    disabled={tankID === -1}
                                    rules={{
                                        required: true,
                                        validate: (val: number) => {return validateTankLevelClose(val, tank!.tankStyle.heightInInches)} 
                                    }}
                                    readOnly={inputMode === InputModeEnum.Haul}
                                    onChange={onLevelChanged}
                                />
                            }

                        </FormProperty>
                        {tank?.isDualPhase &&
                            <FormProperty>
                                {!userState.userSettings?.tankLevelInches ?
                                <FormFeetAndInchesPicker
                                    name="interfaceLevelClose"
                                    label={t("runTicket.interfaceLevel")}
                                    maxFeet={tank ? (tank.tankStyle.heightInInches / 12) : 12}
                                    sx={{
                                        width: 1
                                    }}
                                    disabled={tankID === -1}
                                    rules={{
                                        validate: validateInterfaceLevelClose
                                    }}
                                    readOnly={inputMode === InputModeEnum.Haul}
                                    onChange={onLevelChanged}
                                />
                                :
                                <FormInput
                                    name="interfaceLevelClose"
                                    label={t("runTicket.interfaceLevelInches")}
                                    numeric={true}
                                    sx={{
                                        width: 1
                                    }}
                                    disabled={tankID === -1}
                                    rules={{
                                        required: true,
                                        validate: (val: number) => {return validateInterfaceLevelClose(val, tank!.tankStyle.heightInInches)} 
                                    }}
                                    readOnly={inputMode === InputModeEnum.Haul}
                                    onChange={onLevelChanged}
                                />
                            }

                            </FormProperty>
                        }
                        <FormProperty>
                            <FormDateTimePicker
                                name="completeDateTime"
                                label={t("runTicket.timestamp")}
                                sx={{
                                    width: 1
                                }}
                                rules={{
                                    required: true,
                                    validate: validateEndDate
                                }}
                                disabled={tankID === -1}
                                minDate={minDate}
                                maxDate={maxDate}
                            />
                        </FormProperty>
                        <FormProperty>
                            <FormInput
                                name="temperatureBottom"
                                label={t("runTicket.temperature")}
                                numeric={true}
                                sx={{
                                    width: 1
                                }}
                                disabled={tankID === -1}
                            />
                        </FormProperty>
                        <FormProperty>
                            <FormInput
                                name="sealOn"
                                label={t("runTicket.sealOn")}
                                sx={{
                                    width: 1
                                }}
                                disabled={tankID === -1}
                            />
                        </FormProperty>
                    </FormPropertySection>

                    <FormPropertySection label={t("runTicket.condition")}>
                        <FormProperty>
                            <FormInput
                                name="specificGravity"
                                label={t("runTicket.specificGravity")}
                                numeric={true}
                                sx={{
                                    width: 1
                                }}
                                disabled={tankID === -1}
                            />
                        </FormProperty>
                        <FormProperty>
                            <FormInput
                                name="basicSedimentsAndWater"
                                label={t("runTicket.bsw")}
                                numeric={true}
                                sx={{
                                    width: 1
                                }}
                                disabled={tankID === -1}
                            />
                        </FormProperty>
                    </FormPropertySection>

                    <FormPropertySection label={t("runTicket.estimatedHaul")}>
                        <FormProperty>
                            <FormInput
                                name="tankLevelEstimatedHaul"
                                label={t(tank?.resourceType === ResourceTypeEnum.Oil || tank?.isDualPhase ? "runTicket.oilHaul" : "runTicket.waterHaul")}
                                numeric={true}
                                sx={{
                                    width: 1
                                }}
                                disabled={tankID === -1 || inputMode === InputModeEnum.Level}
                                //readOnly={inputMode === InputModeEnum.Level}
                                units={tank?.resourceType === ResourceTypeEnum.Oil || tank?.isDualPhase ? "BO" : "BW"}
                                onChange={onHaulChanged}
                            />
                        </FormProperty>
                        {tank?.isDualPhase &&
                            <FormProperty>
                                <FormInput
                                    name="interfaceLevelEstimatedHaul"
                                    label={t("runTicket.waterHaul")}
                                    numeric={true}
                                    sx={{
                                        width: 1
                                    }}
                                    disabled={tankID === -1 || inputMode === InputModeEnum.Level}
                                    //readOnly={inputMode === InputModeEnum.Level}
                                    units="BW"
                                    onChange={onHaulChanged}
                                />
                            </FormProperty>
                        }
                        <FormProperty>
                            <FormInput
                                name="purchaserName"
                                label={t("runTicket.purchaser")}
                                sx={{
                                    width: 1
                                }}
                                disabled={tankID === -1}
                            />
                        </FormProperty>
                    </FormPropertySection>

                    <FormPropertySection label="">
                        <FormProperty>
                            <FormInput
                                name="comments"
                                label={t("runTicket.comments")}
                                multiline={true}
                                rows={3}
                                sx={{
                                    width: 1
                                }}
                                disabled={tankID === -1}
                            />
                        </FormProperty>
                    </FormPropertySection>

                    <FormPropertySection label={
                        <Stack spacing={0} direction="row" sx={{
                            alignItems: "center"
                        }}>
                            <Typography component="span" variant="subtitle1" sx={{
                                fontWeight: 600
                            }} >
                                Attachments
                            </Typography>
                            {/* <IconButton color="inherit" onClick={
                                    () => onOpenFiles()
                                }>

                                    <AddPhotoAlternate />
                                </IconButton> */}
                            <IconButton color="inherit" onClick={
                                () => onOpenCamera()
                            }>
                                <AddAPhoto />
                            </IconButton>
                        </Stack>
                    }>

                        {attachments.map((field, index) => (
                            <FormProperty key={field.fieldKey}>
                                <FormAttachmentCard
                                    name={`attachments.${index}`}
                                    index={index}
                                    onAction={onAttachmentAction} />
                            </FormProperty>
                        ))}

                    </FormPropertySection>

                </Form>
                {/* </LoadingZone> */}
            </ResponsiveDialog>
            <FileDownloader ref={fileDownloaderRef} />
            <UnsavedChangesPrompt
                hasChanges={isDirty}
                getUserConfirmation={getUserConfirmation}
            />
            <CameraDialog {...bindCameraDialog(cameraDialog)} />
            <FileViewerDialog {...bindFileViewerDialog(fileViewerDialog)} />
            {/* <FileSelectDialog {...bindDialog(fileSelectDialog)} /> */}
        </>
    );
}

function toIRunticket(runTicket: IRunTicketModel | undefined, tanks: IRunTicketModel.Tank[], data: RunTicketEditDialogFormValuesType) {
    return {
        ...(runTicket ?? {}),
        tank: tanks.find(tank => tank.id === data.tankID),
        ticketNumber: data.ticketNumber,
        purchaserName: data.purchaserName,
        comments: data.comments,
        startDateTime: data.startDateTime?.toISOString(),
        tankLevelOpen: data.tankLevelOpen,
        interfaceLevelOpen: data.interfaceLevelOpen,
        temperatureTop: data.temperatureTop,
        sealOff: data.sealOff,
        completeDateTime: data.completeDateTime?.toISOString(),
        tankLevelClose: data.tankLevelClose,
        interfaceLevelClose: data.interfaceLevelClose,
        temperatureBottom: data.temperatureBottom,
        sealOn: data.sealOn,
        specificGravity: data.specificGravity,
        basicSedimentsAndWater: data.basicSedimentsAndWater,
        attachments: data.attachments,
        tankLevelEstimatedHaul: data.tankLevelEstimatedHaul,
        interfaceLevelEstimatedHaul: data.interfaceLevelEstimatedHaul
    } as IRunTicketModel;
}

function toFormValuesType(runTicket: IRunTicketModel): RunTicketEditDialogFormValuesType {
    return {
        tankID: valueOrDefault(runTicket.tank.id, defaultFormValues.tankID),
        ticketNumber: valueOrDefault(runTicket.ticketNumber, defaultFormValues.ticketNumber),
        purchaserName: valueOrDefault(runTicket.purchaserName, defaultFormValues.purchaserName),
        comments: valueOrDefault(runTicket.comments, defaultFormValues.comments),
        startDateTime: runTicket.startDateTime?.length > 0
            ? new Date(runTicket.startDateTime)
            : defaultFormValues.startDateTime,
        tankLevelOpen: valueOrDefault(runTicket.tankLevelOpen, defaultFormValues.tankLevelOpen),
        interfaceLevelOpen: valueOrDefault(runTicket.interfaceLevelOpen, defaultFormValues.interfaceLevelOpen),
        temperatureTop: valueOrDefault(runTicket.temperatureTop, defaultFormValues.temperatureTop),
        sealOff: valueOrDefault(runTicket.sealOff, defaultFormValues.sealOff),
        completeDateTime: runTicket.completeDateTime?.length > 0
            ? new Date(runTicket.completeDateTime)
            : defaultFormValues.completeDateTime,
        tankLevelClose: valueOrDefault(runTicket.tankLevelClose, defaultFormValues.tankLevelClose),
        interfaceLevelClose: valueOrDefault(runTicket.interfaceLevelClose, defaultFormValues.interfaceLevelClose),
        temperatureBottom: valueOrDefault(runTicket.temperatureBottom, defaultFormValues.temperatureBottom),
        sealOn: valueOrDefault(runTicket.sealOn, defaultFormValues.sealOn),
        specificGravity: valueOrDefault(runTicket.specificGravity, defaultFormValues.specificGravity),
        basicSedimentsAndWater: valueOrDefault(runTicket.basicSedimentsAndWater, defaultFormValues.basicSedimentsAndWater),
        tankLevelEstimatedHaul: defaultFormValues.tankLevelEstimatedHaul,
        interfaceLevelEstimatedHaul: defaultFormValues.interfaceLevelEstimatedHaul,
        attachments: runTicket.attachments
    } as RunTicketEditDialogFormValuesType;
}

export type RunTicketEditDialogState = DialogState<RunTicketEditDialogInitValues, RunTicketEditDialogResult>;
export type BindRunTicketEditDialogReturn = BindDialogReturn<RunTicketEditDialogInitValues, RunTicketEditDialogResult>;

export function useRunTicketEditDialogState(): RunTicketEditDialogState {
    return useDialogState();
}

export function bindRunTicketEditDialog(props: RunTicketEditDialogState): BindRunTicketEditDialogReturn {
    return bindDialog(props);
}
